import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useAppState } from "Context/StateContext";
import { MemberStaffWithoutTimezoneId } from "Models/members/member.staff";
import { useTranslation } from "react-i18next";
import BranchService from "Services/branch.service";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { AccessSettings } from "../models/access.settings";
import { Fleet } from "../models/fleet";
import { FleetType } from "../models/fleet.type";
import { Instruction } from "../models/instruction";
import { ILocationSymbol, IExceptionSymbol, IDescriptiveSymbol } from "../models/inventory";
import { Item } from "../models/item";
import { ItemTypes } from "../models/item.types";
import { JobMode } from "../models/job.mode";
import { Room } from "../models/room";
import { Service } from "../models/service";
import { ServiceBooking as Material } from "../models/service.booking";
import { Shipment } from "../models/shipments";
import { Staff } from "../models/staff";
import FleetTypeService from "../services/fleet.type.service";
import OnBoardingService from "../services/onBoarding.service";
import { checkError } from "../util/checkError";
import { Status, UploadValueType } from "../util/enums";

const useActionOnBoarding = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const getFleetTypeList = async (): Promise<FleetType[] | undefined> => {
    const response = await FleetTypeService.getAll();

    if (response?.data && response?.data?.length > 0) {
      return response.data;
    }

    return undefined;
  };

  const useUpdateDefault = (callback?: () => void) =>
    useMutation(
      ["onboarding.update.default"],
      ({
        companyId,
        uploadType,
        branchId,
      }: {
        companyId?: number;
        uploadType?: UploadValueType;
        branchId?: number;
      }) => OnBoardingService.updateDefault(companyId, uploadType, branchId),
      {
        onSuccess: () => {
          if (callback) callback();
        },
        onError: (error: AxiosError<Error>) => {
          checkError(error, translate("anErrorOccurredTryAgain"));
        },
      }
    );

  const { mutate: updateMutateAccess } = useMutation(
    ["onboarding.update.access"],
    ({ accesses, companyId }: { accesses: AccessSettings[]; companyId: number }) =>
      OnBoardingService.updateAccess(accesses, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.access.error",
        });
      },
    }
  );

  const { mutate: updateMutateFleet } = useMutation(
    ["onboarding.update.fleet"],
    ({ fleet, companyId }: { fleet: Fleet[]; companyId: number }) =>
      OnBoardingService.updateFleetList(fleet, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.fleet.error",
        });
      },
    }
  );

  const { mutate: updateMutateInstructions } = useMutation(
    ["onboarding.update.instructions"],
    ({ instructions, companyId }: { instructions: Instruction[]; companyId: number }) =>
      OnBoardingService.updateInstructions(instructions, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.instructions.error",
        });
      },
    }
  );

  const { mutate: updateMutateMaterials } = useMutation(
    ["onboarding.update.materials"],
    ({ materials, companyId }: { materials: Material[]; companyId: number }) =>
      OnBoardingService.updateMaterials(materials, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.materials.error",
        });
      },
    }
  );

  const { mutate: updateMutateService } = useMutation(
    ["onboarding.update.service"],
    ({ service, companyId }: { service: Service[]; companyId: number }) =>
      OnBoardingService.updateService(service, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.service.error",
        });
      },
    }
  );

  const { mutate: updateMutateMember } = useMutation(
    ["onboarding.update.member"],
    ({ members, companyId }: { members: MemberStaffWithoutTimezoneId[]; companyId: number }) =>
      OnBoardingService.createMembers(members, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.member.error",
        });
      },
    }
  );

  const { mutate: updateMutateItems } = useMutation(
    ["onboarding.update.items"],
    ({ items, companyId }: { items: Item[]; companyId: number }) =>
      OnBoardingService.updateItems(items, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutateExceptions } = useMutation(
    ["onboarding.update.exceptions"],
    ({ exceptions, companyId }: { exceptions: IExceptionSymbol[]; companyId: number }) =>
      OnBoardingService.updateExceptions(exceptions, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutateExceptionLocations } = useMutation(
    ["onboarding.update.exception.locations"],
    ({
      exceptionLocations,
      companyId,
    }: {
      exceptionLocations: ILocationSymbol[];
      companyId: number;
    }) => OnBoardingService.updateExceptionLocation(exceptionLocations, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutatePreparationMethod } = useMutation(
    ["onboarding.update.preparation.methods"],
    ({
      preparationMethods,
      companyId,
    }: {
      preparationMethods: IDescriptiveSymbol[];
      companyId: number;
    }) => OnBoardingService.updatePreparationMethod(preparationMethods, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutateLocations } = useMutation(
    ["onboarding.update.locations"],
    ({ locations, companyId }: { locations: Room[]; companyId: number }) =>
      OnBoardingService.updateLocations(locations, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutatePackageTypes } = useMutation(
    ["onboarding.update.package.types"],
    ({ itemTypes, companyId }: { itemTypes: ItemTypes[]; companyId: number }) =>
      OnBoardingService.updatePackageType(itemTypes, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutateModes } = useMutation(
    ["onboarding.update.modes"],
    ({ modes, companyId }: { modes: JobMode[]; companyId: number }) =>
      OnBoardingService.updateModes(modes, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.staff.error",
        });
      },
    }
  );

  const { mutate: updateMutateShipments } = useMutation(
    ["onboarding.update.shipments"],
    ({ shipments, companyId }: { shipments: Shipment[]; companyId: number }) =>
      OnBoardingService.updateShipments(shipments, companyId),
    {
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"), {
          id: "onboarding.update.shipment.error",
        });
      },
    }
  );

  const updateShipments = (
    shipments: Shipment[],
    companyId: number,
    callback: (shipments: Shipment[]) => void
  ) => {
    updateMutateShipments(
      { shipments, companyId },
      {
        onSuccess: ({ data }) => {
          if (data && data.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateAccess = (
    accesses: AccessSettings[],
    companyId: number,
    callback: (accesses: AccessSettings[]) => void
  ) => {
    updateMutateAccess(
      { accesses, companyId },
      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },

        onError: () => callback([]),
      }
    );
  };

  const updateFleetList = (
    fleet: Fleet[],
    companyId: number,
    callback: (fleet: Fleet[]) => void
  ) => {
    return updateMutateFleet(
      { fleet, companyId },
      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateInstructions = (
    instructions: Instruction[],
    companyId: number,
    callback: (instructions: Instruction[]) => void
  ) => {
    updateMutateInstructions(
      { instructions, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };
  const updateMaterials = (
    materials: Material[],
    companyId: number,
    callback: (materials: Material[]) => void
  ) => {
    updateMutateMaterials(
      { materials, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };
  const updateService = (
    service: Service[],
    companyId: number,
    callback: (service: Service[]) => void
  ) => {
    updateMutateService(
      { service, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateMembers = (
    members: MemberStaffWithoutTimezoneId[],
    companyId: number,
    callback: (staff: Staff[]) => void
  ) => {
    updateMutateMember(
      { members, companyId },
      {
        onSuccess: ({ data }) => {
          if (data?.length) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateItems = (items: Item[], companyId: number, callback: (item: Item[]) => void) => {
    updateMutateItems(
      { items, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateExceptions = (
    exceptions: IExceptionSymbol[],
    companyId: number,
    callback: (item: IExceptionSymbol[]) => void
  ) => {
    updateMutateExceptions(
      { exceptions, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateExceptionLocations = (
    exceptionLocations: ILocationSymbol[],
    companyId: number,
    callback: (item: ILocationSymbol[]) => void
  ) => {
    updateMutateExceptionLocations(
      { exceptionLocations, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updatePreparationMethods = (
    preparationMethods: IDescriptiveSymbol[],
    companyId: number,
    callback: (item: IDescriptiveSymbol[]) => void
  ) => {
    updateMutatePreparationMethod(
      { preparationMethods, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateLocations = (
    locations: Room[],
    companyId: number,
    callback: (item: Room[]) => void
  ) => {
    updateMutateLocations(
      { locations, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updatePackageTypes = (
    itemTypes: ItemTypes[],
    companyId: number,
    callback: (item: ItemTypes[]) => void
  ) => {
    updateMutatePackageTypes(
      { itemTypes, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(data);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const updateModes = (
    modes: JobMode[],
    companyId: number,
    callback: (item: JobMode[]) => void
  ) => {
    updateMutateModes(
      { modes, companyId },

      {
        onSuccess: ({ data }) => {
          if (data && data?.length > 0) {
            callback(modes);
          } else callback([]);
        },
        onError: () => callback([]),
      }
    );
  };

  const getCompanyFirstBranch = () => {
    return useQuery({
      queryKey: ["onboarding.branch.first", currentUser?.companyId],
      queryFn: () =>
        BranchService.getAll({
          page: 0,
          pageSize: 1,
          status: Status.ACTIVE,
          companyId: currentUser?.companyId,
        }).then(({ data }) => data?.at(0)),
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
    });
  };

  return {
    getFleetTypeList,
    getCompanyFirstBranch,
    updateAccess,
    updateFleetList,
    updateInstructions,
    updateMaterials,
    updateService,
    updateMembers,
    updateItems,
    updateExceptions,
    updateExceptionLocations,
    updatePreparationMethods,
    updateLocations,
    updatePackageTypes,
    updateModes,
    useUpdateDefault,
    updateShipments,
  };
};

export default useActionOnBoarding;
